import { inject } from '@angular/core';
import { Auth, User, authState } from './firebase/auth';
import {
  type AuthPipe,
  type AuthPipeGenerator,
  redirectLoggedInTo,
  redirectUnauthorizedTo,
} from './firebase/auth-guard';
import { parseReturnTarget, returnUrlOf } from './return-url';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
  type MaybeAsync,
  type CanActivateFn,
} from '@angular/router';
import { of, map, mergeMap, take, from, catchError, switchMap } from 'rxjs';
import { ProfileService } from '../services/profile.service';
import { SchoolService } from '../services/school.service';
import { RoleService } from '../services/role.service';
import { LicenseApiService } from '../services/license-api.service';
import { StaffService } from '../services/staff.service';
import { loadMunicipalityPlanSelection } from '../pages/onboarding/municipality-plan-selection';

/**
 * `user.reload()` is a round trip to Google on every navigation. Only an
 * unverified user can change state (by clicking the mail link elsewhere), and
 * Firebase updates the local user itself when the email changes.
 */
const reloadIfUnverified = (user: User) =>
  user.emailVerified ? of(undefined) : from(user.reload());

export const redirectLoggedInToHome = () => redirectLoggedInTo(['home']);

export const redirectLoggedInToTarget = () => {
  const redirectAfterLoginJson = localStorage.getItem('redirectAfterLogin');
  if (!redirectAfterLoginJson) {
    return redirectLoggedInTo(['home']);
  }

  const target = JSON.parse(redirectAfterLoginJson) as { path: string };
  localStorage.removeItem('redirectAfterLogin');
  return redirectLoggedInTo(target.path || ['home']);
};

/**
 * `/login` and `/signup` for a visitor who is already logged in: with a
 * `returnUrl` that is an app path straight there, else as `fallback` says.
 * A storefront `returnUrl` («Einloggen» in the website header sends the page
 * it was on) only counts after an actual login: a logged-in visitor clicking
 * «Einloggen» wants the app, not the page they came from. See
 * core/return-url.ts.
 */
export const redirectLoggedInToReturnTarget =
  (fallback: () => AuthPipe): AuthPipeGenerator =>
  next => {
    const target = parseReturnTarget(returnUrlOf(next));
    return target?.kind === 'app' ? redirectLoggedInTo(target.url) : fallback();
  };

export const authGuard = (
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const profileService = inject(ProfileService);
  const auth = inject(Auth);

  return authState(auth).pipe(
    take(1),
    mergeMap(user => {
      if (!user) {
        return of(router.createUrlTree(['/login', { returnUrl: _state.url }]));
      }

      return reloadIfUnverified(user).pipe(
        mergeMap(() => {
          if (!user.emailVerified) {
            return of(router.createUrlTree(['/verify-email']));
          }

          return profileService.getMe(true).pipe(
            map(profile => {
              if (!profile.isComplete) {
                return router.createUrlTree(['/welcome']);
              }
              return true;
            })
          );
        })
      );
    })
  );
};

const checkEmailVerificationStatus = (
  state: RouterStateSnapshot,
  shouldRedirectIfVerified: boolean,
  redirectUrl: (string | { [key: string]: any })[]
) => {
  const router = inject(Router);
  const auth = inject(Auth);

  return authState(auth).pipe(
    take(1),
    mergeMap(user => {
      if (!user) {
        return of(router.createUrlTree(['/login', { returnUrl: state.url }]));
      }

      return reloadIfUnverified(user).pipe(
        map(() => {
          const shouldRedirect = shouldRedirectIfVerified
            ? user.emailVerified
            : !user.emailVerified;

          if (shouldRedirect) {
            return router.createUrlTree(redirectUrl);
          }

          return true;
        })
      );
    })
  );
};

export const verifyEmailGuard = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => checkEmailVerificationStatus(state, true, ['/welcome']);

export const requireVerifiedGuard = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => checkEmailVerificationStatus(state, false, ['/verify-email']);

export const isCurrentUserAuthGuard = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const profileService = inject(ProfileService);
  const auth = inject(Auth);

  return authState(auth).pipe(
    take(1),
    mergeMap(user => {
      if (!user) {
        return of(router.createUrlTree(['/login', { returnUrl: _state.url }]));
      }

      return reloadIfUnverified(user).pipe(
        mergeMap(() => {
          if (!user.emailVerified) {
            return of(router.createUrlTree(['/verify-email']));
          }

          return profileService.getMe(true).pipe(
            map(profile => {
              if (!profile.isComplete) {
                return router.createUrlTree(['/welcome']);
              }

              if (profile.id !== route.paramMap.get('id')) {
                return router.createUrlTree(['/profile/', profile.id]);
              }

              return true;
            })
          );
        })
      );
    })
  );
};

function hrPortalEntityId(route: ActivatedRouteSnapshot): string | null {
  return route.paramMap.get('id') ?? route.paramMap.get('authorityId');
}

function hrToolLockedUrlTree(
  router: Router,
  navigationUrl: string,
  school: { id?: string }
): ReturnType<Router['createUrlTree']> {
  const id = school.id!;
  const fromOrg = navigationUrl.includes('/my-organization/');
  return router.createUrlTree(
    fromOrg
      ? ['/my-organization', id, 'hr-tool-locked']
      : ['/my-schools', id, 'hr-tool-locked']
  );
}

export const hrToolGuard = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const schoolService = inject(SchoolService);
  const roleService = inject(RoleService);

  const entityId = hrPortalEntityId(route);
  if (!entityId) {
    return of(router.createUrlTree(['/']));
  }

  // Non-Owner-Bypass: invited school admins inherit HR access from the paying entity
  // (parent Schulgemeinde). Authority routes never lock here either — the param check
  // distinguishes school vs. authority context.
  const isAuthorityRoute = !!route.paramMap.get('authorityId');
  if (isAuthorityRoute || !roleService.isOwnerOfSchool(entityId)) {
    return of(true);
  }

  return schoolService.getByIdForRouteSnapshot(entityId, route).pipe(
    map(school => {
      if (school?.authorizedForModules?.jobs === false) {
        return hrToolLockedUrlTree(router, state.url, school);
      }
      return true;
    })
  );
};

export const redirectUnauthorizedToLogin = (route: ActivatedRouteSnapshot) => {
  const path = route.pathFromRoot
    .map(v => v.url.map(segment => segment.toString()).join('/'))
    .join('/');
  const params = route.queryParams;
  localStorage.setItem('redirectAfterLogin', JSON.stringify({ path, params }));
  return redirectUnauthorizedTo(['login']);
};

export const schoolAdminGuard = (
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const roleService = inject(RoleService);

  if (roleService.loaded) {
    if (roleService.isSchoolAdmin) {
      return true;
    }
    return router.createUrlTree(['/']);
  }

  return roleService.loadRole().pipe(
    map(role => {
      if (role !== 'fachkraft') {
        return true;
      }
      return router.createUrlTree(['/']);
    })
  );
};

export const schulgemeideGuard = (
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const roleService = inject(RoleService);

  if (roleService.loaded) {
    if (roleService.isSchulgemeinde) {
      return true;
    }
    return router.createUrlTree(['/']);
  }

  return roleService.loadRole().pipe(
    map(role => {
      if (role === 'schulgemeinde') {
        return true;
      }
      return router.createUrlTree(['/']);
    })
  );
};

export const fachkraftOnlyGuard = (
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const roleService = inject(RoleService);

  const jobId = _route.paramMap.get('id');
  const publicApplyFallback = jobId
    ? router.createUrlTree(['/jobs', jobId, 'apply', 'public'])
    : router.createUrlTree(['/']);

  if (roleService.loaded) {
    if (roleService.role === 'fachkraft') {
      return true;
    }
    return publicApplyFallback;
  }

  return roleService.loadRole().pipe(
    map(role => {
      if (role === 'fachkraft') {
        return true;
      }
      return publicApplyFallback;
    })
  );
};

export const canActivateEmbedGuard = (): MaybeAsync<boolean> => {
  return true;
};

/**
 * Schulgemeinde profile wizard (`/onboarding/municipality`): only after the user
 * completed `/onboarding/municipality/plan` (plan persisted in sessionStorage).
 */
export const municipalityOnboardingPlanGuard = (
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
): boolean | ReturnType<Router['createUrlTree']> => {
  const router = inject(Router);
  if (loadMunicipalityPlanSelection()) {
    return true;
  }
  return router.createUrlTree(['/onboarding', 'municipality', 'plan']);
};

/**
 * For paid Schulgemeinde selection (`SCHOOL_S`), verifies the backend exposes a
 * purchasable Schulgemeinde license (`SCHOOL_AUTHORITY_SUBSCRIPTION` per OpenAPI).
 * Does **not** create an order — `POST /v1/licenses/orders` still runs at checkout after profile.
 * Free (`SCHOOL_FREE`) skips this check.
 */
export const municipalityPaidPlanLicenseGuard = (
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const licenseApi = inject(LicenseApiService);
  const sel = loadMunicipalityPlanSelection();
  if (!sel) {
    return router.createUrlTree(['/onboarding', 'municipality', 'plan']);
  }
  if (sel.subscriptionType === 'SCHOOL_FREE') {
    return true;
  }
  return licenseApi.getAvailableLicenses().pipe(
    take(1),
    map(licenses => {
      const ok = licenses.some(
        l => l.license_type === 'SCHOOL_AUTHORITY_SUBSCRIPTION'
      );
      if (ok) {
        return true;
      }
      return router.createUrlTree(['/onboarding', 'municipality', 'plan'], {
        queryParams: { reason: 'license-unavailable' },
      });
    }),
    catchError(() =>
      of(
        router.createUrlTree(['/onboarding', 'municipality', 'plan'], {
          queryParams: { reason: 'license-check-failed' },
        })
      )
    )
  );
};

/**
 * Locks optional/paid-only school profile edit routes (media, we-offer, focus, documents) for free plans.
 * Redirects back to the school profile and sets `?upgrade=1` so the profile can open the upgrade popup.
 */
export const schoolProfileProFieldsGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const schoolService = inject(SchoolService);

  const schoolId = route.paramMap.get('schoolId') ?? route.paramMap.get('id');
  if (!schoolId) {
    return true;
  }

  const baseTree = (() => {
    const authorityId = route.paramMap.get('authorityId');
    const ctxId = route.paramMap.get('id'); // `/my-schools/:id/...`
    if (authorityId) {
      return router.createUrlTree(
        ['/my-organization', authorityId, 'schools', schoolId],
        { queryParams: { upgrade: '1', returnTo: state.url } }
      );
    }
    if (ctxId && route.paramMap.has('schoolId')) {
      return router.createUrlTree(['/my-schools', ctxId, 'schools', schoolId], {
        queryParams: { upgrade: '1', returnTo: state.url },
      });
    }
    return router.createUrlTree(['/schools', schoolId], {
      queryParams: { upgrade: '1', returnTo: state.url },
    });
  })();

  return schoolService.getById(schoolId).pipe(
    take(1),
    switchMap(school => schoolService.profileOptionalSectionsUnlocked(school)),
    map(unlocked => (unlocked ? true : baseTree)),
    catchError(() => of(baseTree))
  );
};

export const staffGuard: CanActivateFn = () => {
  const staff = inject(StaffService);
  const router = inject(Router);
  return staff
    .ensureLoaded()
    .pipe(map(isStaff => (isStaff ? true : router.createUrlTree(['/']))));
};
